/**
 * Tooltip Component
 *
 * Lightweight CSS-only tooltip using Tailwind's group-hover pattern.
 * No external dependencies required (no radix-ui).
 *
 * Usage:
 *   <Tooltip content="Full text here">
 *     <div className="truncate">Short te...</div>
 *   </Tooltip>
 */

import { type ReactNode } from 'react';

interface TooltipProps {
  /** The tooltip content shown on hover */
  content: string;
  /** The element that triggers the tooltip */
  children: ReactNode;
  /** Position of the tooltip relative to the trigger */
  side?: 'top' | 'bottom';
  /** Max width of the tooltip popover */
  maxWidth?: string;
  /** Additional className for the wrapper */
  className?: string;
}

export function Tooltip({
  content,
  children,
  side = 'bottom',
  maxWidth = 'max-w-xs',
  className = '',
}: TooltipProps) {
  return (
    <div className={`group/tooltip relative inline-flex ${className}`}>
      {children}
      <div
        role="tooltip"
        className={`
          pointer-events-none absolute left-1/2 z-50 -translate-x-1/2
          rounded-lg bg-gray-900 px-3 py-2
          text-xs font-medium text-white leading-relaxed
          shadow-lg
          opacity-0 scale-95
          transition-all duration-150
          group-hover/tooltip:opacity-100 group-hover/tooltip:scale-100
          ${maxWidth}
          ${side === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'}
        `}
      >
        {content}
        {/* Arrow */}
        <div
          className={`
            absolute left-1/2 -translate-x-1/2
            h-0 w-0
            border-x-[5px] border-x-transparent
            ${side === 'top'
              ? 'top-full border-t-[5px] border-t-gray-900'
              : 'bottom-full border-b-[5px] border-b-gray-900'
            }
          `}
        />
      </div>
    </div>
  );
}
